import { Component } from '@theme/component';
import { isClickedOutside } from '@theme/utilities';

/**
 * @typedef {object} ShopLookHotspotRefs
 * @property {HTMLDialogElement} dialog
 * @property {HTMLButtonElement} trigger
 */

/**
 * Pin de Shop the look.
 * Hover/focus en desktop; click en touch/mobile.
 * @extends {Component<ShopLookHotspotRefs>}
 */
export class ShopLookHotspot extends Component {
  requiredRefs = ['trigger', 'dialog'];

  /** @type {'mouse' | 'touch' | null} */
  #openedBy = null;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('pointerleave', this.#handlePointerLeave);
    this.addEventListener('focusout', this.#handleFocusOut);
    this.refs.trigger.addEventListener('pointerenter', this.#handlePointerEnter);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('pointerleave', this.#handlePointerLeave);
    this.removeEventListener('focusout', this.#handleFocusOut);
    this.refs.trigger?.removeEventListener('pointerenter', this.#handlePointerEnter);
    this.#removeDismissListeners();
  }

  /**
   * @param {MouseEvent} event
   */
  toggleDialog = (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (this.refs.dialog.open) {
      if (this.#openedBy === 'mouse') return;

      this.closeDialog();
      return;
    }

    this.#openedBy = 'touch';
    this.showDialog();
  };

  showDialog = () => {
    const { dialog, trigger } = this.refs;
    if (dialog.open) return;

    this.#setPlacement();
    dialog.dataset.showing = 'true';
    dialog.show();
    trigger.setAttribute('aria-expanded', 'true');
    document.body.addEventListener('click', this.#lightDismiss);
    document.body.addEventListener('keydown', this.#handleKeydown);
  };

  closeDialog = () => {
    const { dialog, trigger } = this.refs;
    if (!dialog.open) return;

    dialog.close();
    trigger.setAttribute('aria-expanded', 'false');
    delete dialog.dataset.showing;
    delete dialog.dataset.placement;
    this.#openedBy = null;
    this.#removeDismissListeners();
  };

  #setPlacement() {
    const { dialog, trigger } = this.refs;
    const container = this.parentElement;
    if (!(container instanceof HTMLElement)) return;

    if (window.matchMedia('(max-width: 749px)').matches) {
      dialog.dataset.placement = 'mobile';
      return;
    }

    dialog.style.visibility = 'hidden';
    dialog.show();

    const containerRect = container.getBoundingClientRect();
    const triggerRect = trigger.getBoundingClientRect();
    const dialogRect = dialog.getBoundingClientRect();
    dialog.close();
    dialog.style.removeProperty('visibility');

    const spaceRight = containerRect.right - triggerRect.right;
    const spaceLeft = triggerRect.left - containerRect.left;
    const horizontal =
      spaceRight >= dialogRect.width + 10
        ? 'right'
        : spaceLeft >= dialogRect.width + 10
          ? 'left'
          : 'center';
    const vertical =
      containerRect.bottom - triggerRect.bottom >= dialogRect.height + 10
        ? 'bottom'
        : 'top';

    dialog.dataset.placement = `${horizontal},${vertical}`;
  }

  /**
   * @param {PointerEvent} event
   */
  #handlePointerEnter = (event) => {
    if (event.pointerType !== 'mouse') return;

    this.#openedBy = 'mouse';
    this.showDialog();
  };

  /**
   * @param {PointerEvent} event
   */
  #handlePointerLeave = (event) => {
    if (this.#openedBy !== 'mouse') return;
    if (
      event.relatedTarget instanceof Node &&
      this.contains(event.relatedTarget)
    )
      return;
    this.closeDialog();
  };

  /**
   * @param {FocusEvent} event
   */
  #handleFocusOut = (event) => {
    if (
      event.relatedTarget instanceof Node &&
      this.contains(event.relatedTarget)
    )
      return;
    this.closeDialog();
  };

  /**
   * @param {MouseEvent} event
   */
  #lightDismiss = (event) => {
    if (
      isClickedOutside(event, this.refs.dialog) &&
      event.target !== this.refs.trigger
    ) {
      this.closeDialog();
    }
  };

  /**
   * @param {KeyboardEvent} event
   */
  #handleKeydown = (event) => {
    if (event.key !== 'Escape') return;
    this.closeDialog();
    this.refs.trigger.focus();
  };

  #removeDismissListeners() {
    document.body.removeEventListener('click', this.#lightDismiss);
    document.body.removeEventListener('keydown', this.#handleKeydown);
  }
}

if (!customElements.get('shop-look-hotspot')) {
  customElements.define('shop-look-hotspot', ShopLookHotspot);
}
